import { decorate, observable, action, computed } from 'mobx';
import axios from 'axios';
import { CARD_PAYMENT_METHODS } from '../constants/CardPaymentMethod';

class SourceStore {
  constructor() {
    this.authenticatedPayer = false;
    this.authorizeLoading = false;
    this.failLoading = false;
    this.livemode = null;
    this.pageLoadingText = null;
    this.pageLoading = true;
    this.codeId = null;
    this.parameterId = '<none>';
    this.redirectUrl = undefined;
    this.sourceType = '';
    this.source = {
      id: undefined,
      amount: 100,
      cardDeviceDataCollection: {},
      currency: 'PHP',
      organizationId: '',
      type: '',
      failedUrl: '',
      redirect: {},
      status: '',
      successUrl: '',
      threeDSecureStatus: ''
    };
  }

  setAuthorizeLoading(value) {
    this.authorizeLoading = value;
  }

  setFailLoading(value) {
    this.failLoading = value;
  }

  setLivemode(value) {
    this.livemode = value;
  }

  setPageLoadingText(value) {
    this.pageLoadingText = value;
  }

  setPageLoading = value => {
    this.pageLoading = value;
  };

  setParameterId(parameterId) {
    this.parameterId = parameterId;
  }

  setCodeId(value) {
    this.codeId = value;
  }

  setRedirectUrl(value) {
    this.redirectUrl = value;
  }

  setSource(value) {
    const source = value;
    source.attributes = source.attributes || {};
    source.attributes.redirect = source.attributes.redirect || {};
    const data = {
      id: source.id,
      amount: source.attributes.amount,
      currency: source.attributes.currency,
      organizationId: source.attributes.organization_id,
      status: source.attributes.status,
      type: source.attributes.type,
      failedUrl: source.attributes.redirect.failed,
      successUrl: source.attributes.redirect.success
    };

    if ([...CARD_PAYMENT_METHODS, 'three_d_secure'].includes(source.attributes.type)) {
      data.redirect = source.attributes.redirect;
      data.failedUrl = source.attributes.redirect.return_url;
      data.successUrl = source.attributes.redirect.return_url;

      if (CARD_PAYMENT_METHODS.includes(source.attributes.type)) {
        data.cardDeviceDataCollection = {
          accessToken: source.attributes.card_device_data_collection.access_token,
          providerId: source.attributes.card_device_data_collection.provider_id,
          url: source.attributes.card_device_data_collection.url
        };

        data.threeDSecureStatus = source.attributes.three_d_secure_status;
      }
    }

    Object.keys(data).forEach(key => {
      this.source[key] = data[key];
    });
  }

  setSourceType(value) {
    this.sourceType = value;
  }

  async loadData() {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/sources/${this.parameterId}`
      );
      const source = response.data.data;

      this.setSource(source);
      this.setLivemode(source.attributes.livemode);
    } catch (e) {
      this.setPageLoading(false);
      this.setLivemode(false);
      this.setSource({
        id: ''
      });
    }
  }

  async authenticatePayer() {
    try {
      this.setAuthorizeLoading(true);

      this.setPageLoadingText(
        'We are almost done setting up. You may be redirected to your bank’s authentication page.'
      );

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/sources/${this.source.id}/authenticate_payer`
      );

      const source = response.data.data;
      const result = { auth_token_response: source.attributes.redirect.auth_token };

      if (response.status === 200) {
        this.authenticatedPayer = true;
        // TODO: [ENG-14443] Improve logic condition for resuable methods
        if (source.attributes.redirect.provider_auth_url) {
          // Will let us render the Cybersource 3DS 2.0 ACS page for 3DS Challenged
          this.setSource(source);
        } else {
          // Will directly go to our internal /charge API for Cybersource 3DS 2.0 frictionless and non-3DS
          await this.charge(result);
        }
      } else {
        await this.loadData();
      }

      this.setPageLoadingText(null);
      this.setAuthorizeLoading(false);
      this.setPageLoading(false);
    } catch (e) {
      this.setRedirectUrl('');
    }
  }

  charge = async data => {
    try {
      this.setAuthorizeLoading(true);

      await this.loadData();

      if (['chargeable', 'pending'].includes(this.source.status)) {
        const payload = { ...(data || {}) };
        if (this.source.type === 'qrph' && this.codeId) payload.code_id = this.codeId;

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/sources/${this.source.id}/charge`,
          payload
        );

        if (response.status === 200) {
          this.setSource(response.data.data);
        } else {
          await this.loadData();
        }

        this.setAuthorizeLoading(false);
        this.setPageLoading(false);

        this.setRedirectUrl(this.source.successUrl);
      } else {
        this.setRedirectUrl('');
      }
    } catch (e) {
      this.setRedirectUrl('');
    }
  };

  fail = async data => {
    try {
      this.setAuthorizeLoading(true);

      await this.loadData();

      if (['chargeable', 'pending'].includes(this.source.status)) {
        const payload = { ...(data || {}) };
        if (this.source.type === 'qrph' && this.codeId) payload.code_id = this.codeId;

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/sources/${this.source.id}/fail`,
          payload
        );

        if (response.status === 200) {
          this.setSource(response.data.data);
        } else {
          await this.loadData();
        }

        this.setAuthorizeLoading(false);
        this.setPageLoading(false);

        this.setRedirectUrl(this.source.failedUrl);
      } else {
        this.setRedirectUrl('');
      }
    } catch (e) {
      this.setRedirectUrl('');
    }
  };

  async expire() {
    try {
      this.setAuthorizeLoading(true);

      await this.loadData();

      if (
        this.source.status === 'pending' ||
        (this.source.status === 'chargeable' && this.source.type === 'qrph')
      ) {
        const payload = {};
        if (this.source.type === 'qrph' && this.codeId) payload.code_id = this.codeId;

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/sources/${this.source.id}/expire`,
          payload
        );

        if (response.status === 200) {
          this.setSource(response.data.data);
        } else {
          await this.loadData();
        }

        this.setAuthorizeLoading(false);
      } else {
        this.setRedirectUrl('');
      }
    } catch (e) {
      this.setRedirectUrl('');
    }
  }

  async cancel() {
    try {
      this.setAuthorizeLoading(true);

      await this.loadData();

      if (this.source.status === 'pending') {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/sources/${this.source.id}/cancel`
        );

        if (response.status === 200) {
          this.setSource(response.data.data);
          this.setRedirectUrl(this.source.failedUrl);
        } else {
          await this.loadData();
        }

        this.setAuthorizeLoading(false);
      } else {
        this.setRedirectUrl('');
      }
    } catch (e) {
      this.setRedirectUrl('');
    }
  }

  async redirectToFailed() {
    try {
      this.setAuthorizeLoading(true);

      await this.loadData();

      if (this.source.type === 'paymaya') {
        await axios.post(`${process.env.REACT_APP_API_URL}/sources/${this.source.id}/fail`);
      } else if (this.source.type === 'shopee_pay') {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/sources/${this.source.id}/fail`
        );

        if (response.status === 200) {
          this.setSource(response.data.data);
        } else {
          await this.loadData();
        }

        this.setAuthorizeLoading(false);
        this.setPageLoading(false);
      }

      if (this.source.status === 'pending') {
        this.setRedirectUrl(this.source.failedUrl);
      } else {
        this.setRedirectUrl('');
      }
    } catch (e) {
      this.setRedirectUrl('');
    }
  }

  get willPostMessageToWindow() {
    const paymongojs = 'paymongojs://three_d_secure/return_url';
    let flag = false;

    if (this.source.status === 'consumed' && this.source.successUrl === paymongojs) {
      flag = true;
    } else if (
      ['cancelled', 'failed'].includes(this.source.status) &&
      this.source.failedUrl === paymongojs
    ) {
      flag = true;
    }

    return flag;
  }

  get willRedirectToNewPage() {
    return (
      this.redirectUrl !== undefined &&
      this.redirectUrl !== '' &&
      this.redirectUrl !== 'paymongojs://three_d_secure/return_url'
    );
  }

  get formattedAmount() {
    return this.source.amount
      .toFixed(2)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  get processing() {
    return this.authorizeLoading || this.failLoading;
  }
}

decorate(SourceStore, {
  authorizeLoading: observable,
  codeId: observable,
  failLoading: observable,
  livemode: observable,
  pageLoadingText: observable,
  pageLoading: observable,
  parameterId: observable,
  redirectUrl: observable,
  source: observable,
  sourceType: observable,
  loadData: action,
  setAuthorizeLoading: action,
  setCodeId: action,
  setFailLoading: action,
  setLivemode: action,
  setPageLoadingText: action,
  setPageLoading: action,
  setParameterId: action,
  setRedirectUrl: action,
  setSource: action,
  setSourceType: action,
  formattedAmount: computed,
  processing: computed,
  willPostMessageToWindow: computed,
  willRedirectToNewPage: computed
});

export default SourceStore;
